import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { ROLE_LABELS, ROLE_PERMISSIONS, PageGuard } from "@/components/shared/PlanGuard";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Users, Plus, Pencil, Trash2, Shield, CheckCircle2, XCircle,
  Eye, Edit3, Download, PlusCircle, Loader2, Send,
} from "lucide-react";
import InvitePanel from "@/components/team/InvitePanel";

const ROLE_COLORS = {
  admin:      "bg-purple-100 text-purple-700",
  supervisor: "bg-blue-100 text-blue-700",
  worker:     "bg-emerald-100 text-emerald-700",
  auditor:    "bg-gray-100 text-gray-600",
};

const PERMISSION_ACTIONS = [
  { key: "can_view",   icon: Eye,        label: "Veure" },
  { key: "can_create", icon: PlusCircle, label: "Crear" },
  { key: "can_edit",   icon: Edit3,      label: "Editar" },
  { key: "can_delete", icon: Trash2,     label: "Eliminar" },
  { key: "can_export", icon: Download,   label: "Exportar" },
];

function TeamManagementInner() {
  const { currentVenue, userRole, user, loading: venueLoading } = useVenue();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ user_email: "", user_name: "", dni_nie: "", role: "worker", position: "", phone: "", active: true, notes: "", contract_type: "", workday_type: "full_time", weekly_hours: "", daily_hours: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("members"); // members | invite

  const isAdmin = userRole === "superadmin" || userRole === "admin";

  useEffect(() => {
    if (currentVenue) loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVenue]);

  const loadMembers = async () => {
    setLoading(true);
    const [items, invs] = await Promise.all([
      hosys.entities.VenueMember.filter({ venue_id: currentVenue.id }),
      hosys.entities.Invitation.filter({ venue_id: currentVenue.id }),
    ]);
    setMembers(items.sort((a, b) => {
      const order = { admin: 0, supervisor: 1, worker: 2, auditor: 3 };
      return (order[a.role] ?? 9) - (order[b.role] ?? 9);
    }));
    setInvitations(invs.filter(i => i.status === "pending" || i.status === "sent"));
    setLoading(false);
  };

  const openNew = () => {
   setEditing(null);
   setForm({ user_email: "", user_name: "", dni_nie: "", role: "worker", position: "", phone: "", pin: "", active: true, notes: "", contract_type: "", workday_type: "full_time", weekly_hours: "", daily_hours: "", start_date: "", end_date: "" });
   setShowForm(true);
  };

  const openEdit = (m) => {
   setEditing(m);
   // Also load contract data if available
   hosys.entities.WorkerContract.filter({ venue_id: m.venue_id, user_email: m.user_email })
     .then(contracts => {
       const c = contracts[0];
       setForm({ user_email: m.user_email, user_name: m.user_name || "", dni_nie: m.dni_nie || "", role: m.role, position: m.position || "", phone: m.phone || "", pin: m.pin || "", active: m.active !== false, notes: m.notes || "", contract_type: c?.contract_type || "", workday_type: c?.workday_type || "full_time", weekly_hours: c?.weekly_hours || "", daily_hours: c?.daily_hours || "", start_date: c?.start_date || "", end_date: c?.end_date || "" });
       })
       .catch(() => {
       setForm({ user_email: m.user_email, user_name: m.user_name || "", dni_nie: m.dni_nie || "", role: m.role, position: m.position || "", phone: m.phone || "", pin: m.pin || "", active: m.active !== false, notes: m.notes || "", contract_type: "", workday_type: "full_time", weekly_hours: "", daily_hours: "", start_date: "", end_date: "" });
     });
   setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const memberData = { user_email: form.user_email, user_name: form.user_name, dni_nie: form.dni_nie, role: form.role, position: form.position, phone: form.phone, pin: form.pin, active: form.active, notes: form.notes, venue_id: currentVenue.id };
    if (editing) {
      await hosys.entities.VenueMember.update(editing.id, memberData);
    } else {
      await hosys.entities.VenueMember.create(memberData);
      try { await hosys.users.inviteUser(form.user_email, "user"); } catch (err) { console.warn("[TeamManagement] Platform invite failed:", err.message); }
    }
    // Save contract if hours specified
    if (form.weekly_hours || form.daily_hours || form.contract_type) {
      const contractData = { venue_id: currentVenue.id, user_email: form.user_email, user_name: form.user_name, contract_type: form.contract_type || "indefinit", workday_type: form.workday_type || "full_time", weekly_hours: parseFloat(form.weekly_hours) || 0, daily_hours: parseFloat(form.daily_hours) || 0, start_date: form.start_date || null, end_date: form.end_date || null, active: true };
      const existing = await hosys.entities.WorkerContract.filter({ venue_id: currentVenue.id, user_email: form.user_email });
      if (existing[0]) {
        await hosys.entities.WorkerContract.update(existing[0].id, contractData);
      } else {
        await hosys.entities.WorkerContract.create(contractData);
      }
    }
    setShowForm(false);
    await loadMembers();
    setSaving(false);
  };

  const handleToggleActive = async (m) => {
    await hosys.entities.VenueMember.update(m.id, { active: !m.active });
    await loadMembers();
  };

  const handleDelete = async (m) => {
    await hosys.entities.VenueMember.delete(m.id);
    await loadMembers();
  };

  if (venueLoading || loading) return <QuickLoader />;

  if (!isAdmin) {
    return <EmptyState icon={Shield} title="Accés restringit" description="Només els administradors poden gestionar l'equip." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Gestió de l'Equip"
        subtitle={`${currentVenue?.name} · ${members.length} membre${members.length !== 1 ? "s" : ""}`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setActiveTab(activeTab === "invite" ? "members" : "invite")} className="gap-2 border-[#7B2D3F] text-[#7B2D3F]">
              <Send className="w-4 h-4" />
              {invitations.length > 0 && (
                <span className="bg-[#7B2D3F] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{invitations.length}</span>
              )}
              Invitar
            </Button>
            <Button onClick={openNew} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
              <Plus className="w-4 h-4" /> Afegir membre
            </Button>
          </div>
        }
      />

      {/* Invite Panel */}
      {activeTab === "invite" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <InvitePanel venue={currentVenue} currentUser={user} onInviteSent={loadMembers} />
        </div>
      )}

      {/* Role permissions reference */}
      <Card className="p-4 border-0 shadow-sm">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Permisos per rol</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-4 text-gray-500 font-medium">Rol</th>
                {PERMISSION_ACTIONS.map(a => (
                  <th key={a.key} className="text-center px-2 py-2 text-gray-500 font-medium">{a.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(ROLE_LABELS).filter(([k]) => k !== "superadmin").map(([roleKey, roleLabel]) => {
                const perms = ROLE_PERMISSIONS[roleKey];
                return (
                  <tr key={roleKey} className="border-b border-gray-50">
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ROLE_COLORS[roleKey] || "bg-gray-100 text-gray-600"}`}>
                        {roleLabel}
                      </span>
                    </td>
                    {PERMISSION_ACTIONS.map(a => (
                      <td key={a.key} className="text-center px-2 py-2">
                        {perms[a.key]
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mx-auto" />
                          : <XCircle className="w-3.5 h-3.5 text-gray-300 mx-auto" />
                        }
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Members list */}
      {members.length === 0 ? (
        <EmptyState icon={Users} title="Cap membre" description="Afegeix membres a l'equip per assignar-los rols i permisos." />
      ) : (
        <div className="space-y-2">
          {members.map(m => (
            <Card key={m.id} className={`p-4 border-0 shadow-sm transition-all ${m.active === false ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7B2D3F]/20 to-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#7B2D3F] font-bold text-sm">
                    {(m.user_name || m.user_email).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 text-sm">{m.user_name || m.user_email}</p>
                    <Badge className={`${ROLE_COLORS[m.role] || ""} border-0 text-[11px]`}>
                      {ROLE_LABELS[m.role] || m.role}
                    </Badge>
                    {m.active === false && (
                      <Badge className="bg-gray-100 text-gray-500 border-0 text-[11px]">Inactiu</Badge>
                    )}
                    {invitations.some(i => i.invited_email === m.user_email) && (
                      <Badge className="bg-yellow-100 text-yellow-700 border-0 text-[11px] gap-1">
                        <Send className="w-2.5 h-2.5" /> Invitació pendent
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {m.user_email}
                    {m.position && ` · ${m.position}`}
                    {m.phone && ` · ${m.phone}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Switch
                    checked={m.active !== false}
                    onCheckedChange={() => handleToggleActive(m)}
                    className="scale-75"
                  />
                  <Button variant="ghost" size="icon" onClick={() => openEdit(m)} className="h-8 w-8 text-gray-400 hover:text-[#7B2D3F]">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {m.user_email !== user?.email && (
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(m)} className="h-8 w-8 text-gray-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar membre" : "Afegir membre a l'equip"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={form.user_email}
                  onChange={e => setForm(f => ({ ...f, user_email: e.target.value }))}
                  placeholder="email@exemple.com"
                  disabled={!!editing}
                />
              </div>
              <div className="col-span-2">
                <Label>Nom complet</Label>
                <Input value={form.user_name} onChange={e => setForm(f => ({ ...f, user_name: e.target.value }))} placeholder="Nom i cognoms" />
              </div>
              <div className="col-span-2">
                <Label>DNI / NIE <span className="text-[10px] text-amber-600 font-normal">(obligatori per inspecció de treball)</span></Label>
                <Input value={form.dni_nie} onChange={e => setForm(f => ({ ...f, dni_nie: e.target.value }))} placeholder="Ex: 12345678A o X1234567B" />
              </div>
              <div>
                <Label>Rol *</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="worker">Treballador</SelectItem>
                    <SelectItem value="auditor">Auditor (lectura)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Càrrec / Posició</Label>
                <Input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} placeholder="Ex: Cap de cuina" />
              </div>
              <div>
                <Label>Telèfon</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="6XX XXX XXX" />
              </div>
              <div>
                <Label>PIN (4 dígits) *</Label>
                <Input
                  type="text"
                  maxLength="4"
                  value={form.pin}
                  onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                  placeholder="0000"
                  pattern="\d{4}"
                />
              </div>
              <div className="col-span-2">
                <div className="border-t border-gray-100 pt-3 mt-1">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Contracte laboral</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Tipus contracte</Label>
                      <Select value={form.contract_type} onValueChange={v => setForm(f => ({ ...f, contract_type: v }))}>
                        <SelectTrigger><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="indefinit">Indefinit</SelectItem>
                          <SelectItem value="temporal">Temporal</SelectItem>
                          <SelectItem value="formacio">Formació i aprenentatge</SelectItem>
                          <SelectItem value="obra_servei">Obra i servei</SelectItem>
                          <SelectItem value="interinitat">Interinitat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipus jornada</Label>
                      <Select value={form.workday_type} onValueChange={v => setForm(f => ({ ...f, workday_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">Jornada completa</SelectItem>
                          <SelectItem value="part_time">Jornada parcial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Hores setmanals</Label>
                      <Input type="number" min="1" max="40" value={form.weekly_hours} onChange={e => setForm(f => ({ ...f, weekly_hours: e.target.value }))} placeholder="Ex: 40" />
                    </div>
                    <div>
                      <Label>Hores diàries</Label>
                      <Input type="number" min="1" max="12" step="0.5" value={form.daily_hours} onChange={e => setForm(f => ({ ...f, daily_hours: e.target.value }))} placeholder="Ex: 8" />
                    </div>
                    <div>
                      <Label>Data incorporació</Label>
                      <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Data fi contracte <span className="text-[10px] text-gray-400 font-normal">(si és temporal)</span></Label>
                      <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
                <Label className="text-sm">Membre actiu</Label>
              </div>
            </div>

            {/* Role permissions preview */}
            {form.role && (
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs font-bold text-gray-500 mb-2">Permisos del rol {ROLE_LABELS[form.role]}:</p>
                <div className="flex flex-wrap gap-1.5">
                  {PERMISSION_ACTIONS.map(a => {
                    const allowed = ROLE_PERMISSIONS[form.role]?.[a.key];
                    return (
                      <span key={a.key} className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        allowed ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"
                      }`}>
                        {allowed ? <CheckCircle2 className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
                        {a.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={saving || !form.user_email || !form.role || !form.pin || form.pin.length !== 4} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Desar canvis" : "Afegir membre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamManagement() {
  return (
    <PageGuard moduleCode="team">
      <TeamManagementInner />
    </PageGuard>
  );
}

export default TeamManagement;